import { all, call, put, select, takeLatest } from 'redux-saga/effects'
import * as actions from './actions'
import * as api from './api'
import * as shopsSelectors from './selectors'
import {
  formatCampaignForRequests,
  formatShopPrograms,
  formatPartnerOffersFromResponse,
  formatConquestAdsFromResponse,
} from './formatters'
import { FIELD_NAMES } from 'const/forms'
import { formatSetChangesForRequest } from 'helpers/formatters/images'
import { ENTITY_NAMES } from 'const/api'
import { resetValues } from 'reducers/forms/actions'

function* fetchShopsWorker({ payload }) {
  try {
    const { data } = yield call(api.getShops, payload)
    yield put(
      actions.fetchShopsSuccess({
        ...payload,
        shops: data.data,
      })
    )
  } catch (error) {
    console.error(error)
    yield put(actions.fetchShopsFailure())
  }
}

function* fetchLocalizedShopsWorker({ payload }) {
  try {
    yield put(actions.startSaving())
    const { data } = yield call(api.getLocalizedShops, payload)
    yield put(
      actions.fetchLocalizedShopsSuccess({
        ...payload,
        localizedShops: data,
      })
    )
  } catch (error) {
    console.error(error)
    yield put(actions.fetchLocalizedShopsFailure())
  } finally {
    yield put(actions.finishSaving())
  }
}

function* fetchShopCampaignDealsWorker({ payload }) {
  try {
    yield put(actions.startSaving())
    const body = formatCampaignForRequests(payload)
    const { data } = yield call(api.getShopCampaignDeals, payload)

    yield put(
      actions.fetchShopCampaignDealsSuccess({
        ...body,
        campaignDeals: data,
      })
    )
  } catch (error) {
    console.error(error)
    yield put(actions.fetchLocalizedShopsFailure())
  } finally {
    yield put(actions.finishSaving())
  }
}

function* fetchShopImageSetsWorker({ payload }) {
  try {
    yield put(actions.startSaving())
    const { data } = yield call(api.getShopImageSets, payload)

    yield put(
      actions.fetchShopImageSetsSuccess({
        ...payload,
        imageSets: data,
      })
    )
  } catch (error) {
    console.error(error)
    yield put(actions.fetchShopImageSetsFailure())
  } finally {
    yield put(actions.finishSaving())
  }
}

function* fetchShopAdPackagesWorker({ payload }) {
  try {
    yield put(actions.startSaving())
    const { data } = yield call(api.getShopAdPackages, payload)

    yield put(
      actions.fetchShopAdPackagesSuccess({
        ...payload,
        adPackages: data,
      })
    )
  } catch (error) {
    console.error(error)
    yield put(actions.fetchShopAdPackagesFailure())
  } finally {
    yield put(actions.finishSaving())
  }
}

function* fetchShopOffersWorker({ payload }) {
  try {
    yield put(actions.startSaving())

    const { data } = yield call(api.getShopOffers, payload) || {}
    yield put(
      actions.fetchShopOffersSuccess({
        ...payload,
        shopOffers: data,
      })
    )
  } catch (error) {
    console.error(error)
    yield put(actions.fetchShopOffersFailure())
  } finally {
    yield put(actions.finishSaving())
  }
}

function* fetchShopProgramsWorker({ payload }) {
  try {
    const { data } = yield call(api.getShopPrograms, payload)

    yield put(
      actions.fetchShopProgramsSuccess({
        ...payload,
        programs: formatShopPrograms(data),
      })
    )

    yield put(actions.fetchShopProgramsFailure())
  } finally {
    yield put(actions.finishSaving())
  }
}

function* updateShopWorker({ payload }) {
  try {
    const { data } = yield call(api.updateShop, payload)
    const currentStore = yield select(shopsSelectors.getCurrentShopId)
    if (data) {
      yield put(actions.updateShopSuccess(payload))
      yield put(actions.fetchLocalizedShops({ search: currentStore }))
    } else {
      yield put(actions.updateShopFailure())
    }
  } catch (error) {
    console.error(error)
    yield put(actions.updateShopFailure())
  }
}

function* updateLocalizedShopWorker({ payload }) {
  try {
    const { data } = yield call(api.updateLocalizedShop, payload)
    if (data) {
      yield put(actions.updateLocalizedShopSuccess(payload))
    } else {
      yield put(actions.updateLocalizedShopFailure())
    }
  } catch (error) {
    console.error(error)
    yield put(actions.updateLocalizedShopFailure())
  }
}

function* updateShopCouponsSettingsWorker({ payload }) {
  try {
    const { data } = yield call(api.updateShopCouponsSettings, {
      id: payload.id,
      data: payload.data,
    })

    if (data) {
      const currentStore = yield select(shopsSelectors.getCurrentShopId)

      yield put(actions.updateShopCouponsSettingsSuccess(data))
      yield put(actions.fetchLocalizedShops({ search: currentStore }))
    }
  } catch (error) {
    console.error(error)
    yield put(actions.updateShopCouponsSettingsFailure())
  }
}

function* createShopCampaignWorker({ payload }) {
  try {
    const data = yield call(api.createShopCampaign, payload)
    if (data) {
      yield put(actions.createShopCampaignSuccess(payload))
    } else {
      yield put(actions.createShopCampaignFailure())
    }
  } catch (error) {
    console.error(error)
    yield put(actions.createShopCampaignFailure())
  }
}

function* updateShopCampaignWorker({ payload }) {
  try {
    const { data: body } = payload
    const data = yield call(api.updateShopCampaign, body)
    if (data) {
      yield put(actions.updateShopCampaignSuccess(body))
    } else {
      yield put(actions.updateShopCampaignFailure())
    }
  } catch (error) {
    console.error(error)
    yield put(actions.updateShopCampaignFailure())
  }
}

function* createShopOfferWorker({ payload }) {
  try {
    const data = yield call(api.createShopOffer, payload)
    if (data) {
      yield put(actions.createShopOfferSuccess(payload))
    } else {
      yield put(actions.createShopOfferFailure())
    }
  } catch (error) {
    console.error(error)
    yield put(actions.createShopOfferFailure())
  }
}

function* updateShopOfferWorker({ payload }) {
  try {
    const data = yield call(api.updateShopOffer, payload)
    if (data) {
      yield put(actions.updateShopOfferSuccess(payload))
    } else {
      yield put(actions.updateShopOfferFailure())
    }
  } catch (error) {
    console.error(error)
    yield put(actions.updateShopOfferFailure())
  }
}

function* deleteCampaignWorker({ payload }) {
  try {
    const data = yield call(api.deleteCampaign, payload)
    if (data) {
      yield put(actions.deleteCampaignSuccess(payload))
    } else {
      yield put(actions.deleteCampaignFailure())
    }
  } catch (error) {
    console.error(error)
    yield put(actions.deleteCampaignFailure())
  }
}

function* deleteAdPackageWorker({ payload }) {
  try {
    const data = yield call(api.deleteAdPackage, payload)
    if (data) {
      yield put(actions.deleteAdPackageSuccess(payload))
    } else {
      yield put(actions.deleteAdPackageFailure())
    }
  } catch (error) {
    console.error(error)
    yield put(actions.deleteAdPackageFailure())
  }
}

function* duplicateAdPackageWorker({ payload }) {
  try {
    const data = yield call(api.duplicateAdPackage, payload)
    if (data) {
      yield put(actions.duplicateAdPackageSuccess(payload))
    } else {
      yield put(actions.duplicateAdPackageFailure())
    }
  } catch (error) {
    console.error(error)
    yield put(actions.duplicateAdPackageFailure())
  }
}

function* deleteShopOfferWorker({ payload }) {
  try {
    const data = yield call(api.deleteShopOffer, payload)
    if (data) {
      yield put(actions.deleteShopOfferSuccess(payload))
    } else {
      yield put(actions.deleteShopOfferFailure())
    }
  } catch (error) {
    console.error(error)
    yield put(actions.deleteShopOfferFailure())
  }
}

function* deleteShopAllOffersWorker({ payload }) {
  try {
    const data = yield call(api.deleteShopAllOffers, payload)
    if (data) {
      yield put(actions.deleteShopAllOffersSuccess(payload))
    } else {
      yield put(actions.deleteShopAllOffersFailure())
    }
  } catch (error) {
    console.error(error)
    yield put(actions.deleteShopAllOffersFailure())
  }
}

function* createShopGroupWorker({ payload }) {
  try {
    yield put(actions.startSaving())
    const data = yield call(api.createShopGroup, payload)
    if (data) {
      yield put(actions.createShopGroupSuccess())
    } else {
      yield put(actions.createShopGroupFailure())
    }
  } catch (error) {
    console.error(error)
    yield put(actions.createShopGroupFailure())
  } finally {
    yield put(actions.finishSaving())
  }
}

function* updateShopGroupWorker({ payload }) {
  try {
    yield put(actions.startSaving())
    const data = yield call(api.updateShopGroup, payload)
    if (data) {
      yield put(actions.updateShopGroupSuccess())
    } else {
      yield put(actions.updateShopGroupFailure())
    }
  } catch (error) {
    console.error(error)
    yield put(actions.updateShopGroupFailure())
  } finally {
    yield put(actions.finishSaving())
  }
}

function* deleteShopGroupWorker({ payload }) {
  try {
    yield put(actions.startSaving())
    const data = yield call(api.deleteShopGroup, payload.values)
    if (data) {
      yield put(actions.deleteShopGroupSuccess())
    } else {
      yield put(actions.deleteShopGroupFailure())
    }
  } catch (error) {
    console.error(error)
    yield put(actions.deleteShopGroupFailure())
  } finally {
    yield put(actions.finishSaving())
  }
}

function* createShopCategoryWorker({ payload }) {
  try {
    yield put(actions.startSaving())
    const data = yield call(api.createShopCategory, payload)
    if (data) {
      yield put(actions.createShopCategorySuccess())
    } else {
      yield put(actions.createShopCategoryFailure())
    }
  } catch (error) {
    console.error(error)
    yield put(actions.createShopCategoryFailure())
  } finally {
    yield put(actions.finishSaving())
  }
}

function* updateShopCategoryWorker({ payload }) {
  try {
    yield put(actions.startSaving())
    const data = yield call(api.updateShopCategory, payload)
    if (data) {
      yield put(actions.updateShopCategorySuccess())
    } else {
      yield put(actions.updateShopCategoryFailure())
    }
  } catch (error) {
    console.error(error)
    yield put(actions.updateShopCategoryFailure())
  } finally {
    yield put(actions.finishSaving())
  }
}

function* deleteShopCategoryWorker({ payload }) {
  try {
    yield put(actions.startSaving())
    const data = yield call(api.deleteShopCategory, payload.values)
    if (data) {
      yield put(actions.deleteShopCategorySuccess())
    } else {
      yield put(actions.deleteShopCategoryFailure())
    }
  } catch (error) {
    console.error(error)
    yield put(actions.deleteShopCategoryFailure())
  } finally {
    yield put(actions.finishSaving())
  }
}

function* updateShopImageSetsWorker({ payload }) {
  try {
    let requests = []

    yield put(actions.startSaving())
    const requestData = formatSetChangesForRequest(payload)
    requests = requests.concat(
      requestData.map((data) => call(api.request, data))
    )

    if (payload.values.hasOwnProperty(FIELD_NAMES.logo)) {
      requests.push(
        call(api.updateEntity, {
          id: payload.shopId,
          type: ENTITY_NAMES.shops,
          attributes: {
            [FIELD_NAMES.logo]: payload.values[FIELD_NAMES.logo],
          },
        })
      )
    }
    if (requests.length) {
      yield all(requests)

      yield put(resetValues())

      yield put(actions.resetShopImageSets())

      yield put(actions.fetchShopImageSets({ id: payload.shopId }))
    }
  } finally {
    yield put(actions.finishSaving())
  }
}

// SEO Descriptions workers
function* fetchShopSeoDescriptionsWorker({ payload }) {
  try {
    yield put(actions.startSaving())

    const { data } = yield call(api.getShopSeoDescriptions, payload) || {}
    yield put(
      actions.fetchShopSeoDescriptionsSuccess({
        ...payload,
        seoDescriptions: data,
      })
    )
  } catch (error) {
    console.error(error)
    yield put(actions.fetchShopSeoDescriptionsFailure())
  } finally {
    yield put(actions.finishSaving())
  }
}

function* createShopSeoDescriptionWorker({ payload }) {
  try {
    const data = yield call(api.createShopSeoDescription, payload)
    if (data) {
      yield put(actions.createShopSeoDescriptionSuccess(payload))
    } else {
      yield put(actions.createShopSeoDescriptionFailure())
    }
  } catch (error) {
    console.error(error)
    yield put(actions.createShopSeoDescriptionFailure())
  }
}

function* updateShopSeoDescriptionWorker({ payload }) {
  try {
    const data = yield call(api.updateShopSeoDescription, payload)
    if (data) {
      yield put(actions.updateShopSeoDescriptionSuccess(payload))
    } else {
      yield put(actions.updateShopSeoDescriptionFailure())
    }
  } catch (error) {
    console.error(error)
    yield put(actions.updateShopSeoDescriptionFailure())
  }
}

function* deleteShopSeoDescriptionWorker({ payload }) {
  try {
    const data = yield call(api.deleteShopSeoDescription, payload)
    if (data) {
      yield put(actions.deleteShopSeoDescriptionSuccess(payload))
    } else {
      yield put(actions.deleteShopSeoDescriptionFailure())
    }
  } catch (error) {
    console.error(error)
    yield put(actions.deleteShopSeoDescriptionFailure())
  }
}

// Partner Offers workers
function* fetchPartnerOffersWorker({ payload }) {
  try {
    const response = yield call(api.getPartnerOffers, payload)
    if (response?.data) {
      const formatted = formatPartnerOffersFromResponse(response.data)
      yield put(
        actions.fetchPartnerOffersSuccess({
          partnerOffers: formatted.data,
          pageCount: formatted.pageCount,
          page: formatted.page,
        })
      )
    }
  } catch (error) {
    console.error(error)
    yield put(actions.fetchPartnerOffersFailure())
  }
}

function* createPartnerOfferWorker({ payload }) {
  try {
    const data = yield call(api.createPartnerOffer, payload)
    if (data) {
      yield put(actions.createPartnerOfferSuccess(payload))
    } else {
      yield put(actions.createPartnerOfferFailure())
    }
  } catch (error) {
    console.error(error)
    yield put(actions.createPartnerOfferFailure())
  }
}

function* updatePartnerOfferWorker({ payload }) {
  try {
    const data = yield call(api.updatePartnerOffer, payload)
    if (data) {
      yield put(actions.updatePartnerOfferSuccess(payload))
    } else {
      yield put(actions.updatePartnerOfferFailure())
    }
  } catch (error) {
    console.error(error)
    yield put(actions.updatePartnerOfferFailure())
  }
}

function* deletePartnerOfferWorker({ payload }) {
  try {
    const data = yield call(api.deletePartnerOffer, payload)
    if (data) {
      yield put(actions.deletePartnerOfferSuccess(payload))
    } else {
      yield put(actions.deletePartnerOfferFailure())
    }
  } catch (error) {
    console.error(error)
    yield put(actions.deletePartnerOfferFailure())
  }
}

// Conquest Ads workers
function* fetchConquestAdsWorker({ payload }) {
  try {
    const response = yield call(api.getConquestAds, payload)
    if (response?.data) {
      const formatted = formatConquestAdsFromResponse(response.data)
      yield put(
        actions.fetchConquestAdsSuccess({
          conquestAds: formatted.data,
          pageCount: formatted.pageCount,
          page: formatted.page,
        })
      )
    }
  } catch (error) {
    console.error(error)
    yield put(actions.fetchConquestAdsFailure())
  }
}

function* createConquestAdWorker({ payload }) {
  try {
    const data = yield call(api.createConquestAd, payload)
    if (data) {
      yield put(actions.createConquestAdSuccess(payload))
    } else {
      yield put(actions.createConquestAdFailure())
    }
  } catch (error) {
    console.error(error)
    yield put(actions.createConquestAdFailure())
  }
}

function* updateConquestAdWorker({ payload }) {
  try {
    const data = yield call(api.updateConquestAd, payload)
    if (data) {
      yield put(actions.updateConquestAdSuccess(payload))
    } else {
      yield put(actions.updateConquestAdFailure())
    }
  } catch (error) {
    console.error(error)
    yield put(actions.updateConquestAdFailure())
  }
}

function* deleteConquestAdWorker({ payload }) {
  try {
    const data = yield call(api.deleteConquestAd, payload)
    if (data) {
      yield put(actions.deleteConquestAdSuccess(payload))
    } else {
      yield put(actions.deleteConquestAdFailure())
    }
  } catch (error) {
    console.error(error)
    yield put(actions.deleteConquestAdFailure())
  }
}

export default function* () {
  yield all([
    takeLatest(actions.fetchShops, fetchShopsWorker),
    takeLatest(actions.fetchLocalizedShops, fetchLocalizedShopsWorker),
    takeLatest(actions.fetchShopCampaignDeals, fetchShopCampaignDealsWorker),
    takeLatest(actions.fetchShopImageSets, fetchShopImageSetsWorker),
    takeLatest(actions.fetchShopAdPackages, fetchShopAdPackagesWorker),
    takeLatest(actions.fetchShopPrograms, fetchShopProgramsWorker),
    takeLatest(actions.updateShop, updateShopWorker),
    takeLatest(actions.updateLocalizedShop, updateLocalizedShopWorker),
    takeLatest(
      actions.updateShopCouponsSettings,
      updateShopCouponsSettingsWorker
    ),
    takeLatest(actions.createShopCampaign, createShopCampaignWorker),
    takeLatest(actions.updateShopCampaign, updateShopCampaignWorker),
    takeLatest(actions.fetchShopOffers, fetchShopOffersWorker),
    takeLatest(actions.createShopOffer, createShopOfferWorker),
    takeLatest(actions.updateShopOffer, updateShopOfferWorker),
    takeLatest(actions.deleteCampaign, deleteCampaignWorker),
    takeLatest(actions.deleteAdPackage, deleteAdPackageWorker),
    takeLatest(actions.duplicateAdPackage, duplicateAdPackageWorker),
    takeLatest(actions.deleteShopOffer, deleteShopOfferWorker),
    takeLatest(actions.deleteShopAllOffers, deleteShopAllOffersWorker),
    takeLatest(actions.createShopGroup, createShopGroupWorker),
    takeLatest(actions.updateShopGroup, updateShopGroupWorker),
    takeLatest(actions.deleteShopGroup, deleteShopGroupWorker),
    takeLatest(actions.createShopCategory, createShopCategoryWorker),
    takeLatest(actions.updateShopCategory, updateShopCategoryWorker),
    takeLatest(actions.deleteShopCategory, deleteShopCategoryWorker),
    takeLatest(actions.updateShopImageSets, updateShopImageSetsWorker),
    takeLatest(actions.fetchShopSeoDescriptions, fetchShopSeoDescriptionsWorker),
    takeLatest(actions.createShopSeoDescription, createShopSeoDescriptionWorker),
    takeLatest(actions.updateShopSeoDescription, updateShopSeoDescriptionWorker),
    takeLatest(actions.deleteShopSeoDescription, deleteShopSeoDescriptionWorker),
    takeLatest(actions.fetchPartnerOffers, fetchPartnerOffersWorker),
    takeLatest(actions.createPartnerOffer, createPartnerOfferWorker),
    takeLatest(actions.updatePartnerOffer, updatePartnerOfferWorker),
    takeLatest(actions.deletePartnerOffer, deletePartnerOfferWorker),
    takeLatest(actions.fetchConquestAds, fetchConquestAdsWorker),
    takeLatest(actions.createConquestAd, createConquestAdWorker),
    takeLatest(actions.updateConquestAd, updateConquestAdWorker),
    takeLatest(actions.deleteConquestAd, deleteConquestAdWorker),
  ])
}
